import { resolveColRail } from "../constants/dispersions.constants";
import { BrebKeyType, isBrebKeyValid } from "../services/paymentsPayout.service";

/**
 * What the Bre-B directory said, reduced to what the gate needs to know.
 *
 * `inactive` is NOT the resolution state's own discriminant — the directory answers 200 for a key it knows
 * and reports `ACTIVE` / `INACTIVE` / … inside the body, so both of those arrive as `resolved`. Collapsing
 * them lost the distinction before either gate could see it, and the gate's one permitting branch is
 * `resolved`: a key the directory positively described as not active passed, while the confirmation card two
 * inches above said in so many words that the send would be rejected. Derived by `brebDirectoryOutcome`.
 */
export type BrebDirectoryOutcome = "idle" | "loading" | "resolved" | "inactive" | "not_found" | "unavailable";

export interface ColombiaDestinationMessages {
  recipientNameRequired: string;
  tfAccountNumberInvalid: string;
  tfBankRequired: string;
  tfDocNumberInvalid: string;
  brebKeyInvalid: string;
  brebNotFound: string;
  brebInactive: string;
  brebUnavailable: string;
}

/**
 * Step-2 destination rules for Colombia, as a pure function so they can be tested.
 *
 * The two rails ask for the recipient in opposite ways and the difference is contractual, not stylistic:
 *
 * - **TRANSFIYA** needs a name. `TransfiyaDestinationDto` declares it required, the backend re-checks it, and
 *   the provider forwards it as the ACH name. This check used to be shared with Bre-B one level up; it lives
 *   inside the branch now, which is the whole reason this function has tests — removing the shared line
 *   without moving it would have dropped Transfiya's validation in silence.
 * - **BREB** needs no name, because nobody can type one: the directory owns it. Which makes the directory's
 *   answer part of the gate — a key it refuses, or cannot be asked about, has no recipient, and a payout with
 *   no confirmed recipient does not go out.
 *
 * `loading` deliberately passes HERE: it lasts one debounce over an already format-valid key, and holding
 * Continue disabled for it would read as a broken button. What makes that safe is the separate step-3 gate
 * (`recipientBlock` in `useDispersionFormErrors`), where only `resolved` passes — so an answer that arrives
 * after Continue was pressed still stops the authorization. These step-2 rules are NOT that gate, and the
 * earlier claim here that step 3 re-read the cache was simply wrong: nothing did.
 */
export function validateColombiaDestination(
  recipientFields: Record<string, unknown> | null | undefined,
  directory: BrebDirectoryOutcome,
  messages: ColombiaDestinationMessages,
): Record<string, string> {
  const errors: Record<string, string> = {};

  if (resolveColRail(recipientFields) === "TRANSFIYA") {
    const beneficiary = String(recipientFields?.beneficiary ?? "");
    const accountNumber = String(recipientFields?.transfiya_account_number ?? "");
    const bankCode = String(recipientFields?.transfiya_bank_code ?? "");
    const docNumber = String(recipientFields?.transfiya_document_number ?? "");

    if (beneficiary.trim().length < 2) errors.beneficiary = messages.recipientNameRequired;
    if (!/^\d{4,34}$/.test(accountNumber)) {
      errors.transfiya_account_number = messages.tfAccountNumberInvalid;
    }
    if (!bankCode) errors.transfiya_bank_code = messages.tfBankRequired;
    if (!/^\d{4,20}$/.test(docNumber)) errors.transfiya_document_number = messages.tfDocNumberInvalid;
    return errors;
  }

  const keyTypeRaw = String(recipientFields?.breb_key_type ?? "").toUpperCase();
  const keyValue = String(recipientFields?.breb_key_value ?? "");
  const keyType = (Object.values(BrebKeyType) as string[]).includes(keyTypeRaw)
    ? (keyTypeRaw as BrebKeyType)
    : null;

  if (!keyType || !isBrebKeyValid(keyType, keyValue)) {
    errors.breb_key_value = messages.brebKeyInvalid;
  } else if (directory === "not_found") {
    errors.breb_key_value = messages.brebNotFound;
  } else if (directory === "inactive") {
    errors.breb_key_value = messages.brebInactive;
  } else if (directory === "unavailable") {
    errors.breb_key_value = messages.brebUnavailable;
  }

  return errors;
}

export interface BrebAuthorizationMessages {
  brebNotFound: string;
  brebInactive: string;
  brebUnavailable: string;
  brebChecking: string;
}

/**
 * Whether the recipient is confirmed enough to AUTHORIZE the payout — the step-3 half of the Bre-B gate,
 * returned as a message to render or null.
 *
 * Why it is a second rule and not the one above: the step-2 rules let `loading` through, so the operator can
 * press Continue during the debounce, and the directory's real answer then arrived on a screen with no gate
 * at all. The Authorize button stayed enabled over a recipient Bre-B had refused.
 *
 * **Only `resolved` passes**, written as that single equality rather than as a list of the bad statuses, so a
 * status added to `BrebDirectoryOutcome` later blocks by default rather than slipping through.
 *
 * Transfiya returns null: it carries a typed name, which the step-2 rules already require, and the directory
 * is never consulted for it.
 */
export function resolveBrebAuthorizationBlock(
  recipientFields: Record<string, unknown> | null | undefined,
  directory: BrebDirectoryOutcome,
  messages: BrebAuthorizationMessages,
): string | null {
  if (resolveColRail(recipientFields) === "TRANSFIYA") return null;
  if (directory === "resolved") return null;
  if (directory === "not_found") return messages.brebNotFound;
  if (directory === "inactive") return messages.brebInactive;
  if (directory === "unavailable") return messages.brebUnavailable;
  return messages.brebChecking;
}
